import {Box, Flex, Skeleton} from '@chakra-ui/react';
import {SettingsAdjust} from '@carbon/icons-react';
import {ObjectId} from 'bson';
import React, {useMemo} from 'react';
import {TagResponse} from '../../shared/tags';
import {useDynamicListMembership, useTags} from '../../hooks/api/tags';
import {filterNull} from '../../shared/util';

export const ListPill = ({list}: {list: TagResponse}) => (
  <Flex
    alignItems="center"
    borderWidth="1px"
    borderColor="kgray.200"
    borderRadius={40}
    color="kgray.400"
    fontSize={12}
    gap={1}
    px={2}
    py={0.5}
    whiteSpace="nowrap"
  >
    {list.type === 'dynamic' && (
      <Box
        as={SettingsAdjust}
        boxSize="12px"
        color="kgray.300"
        flexShrink={0}
      />
    )}
    {list.name}
  </Flex>
);

const _ListsRenderer = ({
  accountId,
  staticListIds,
  emptyState,
}: {
  accountId: ObjectId;
  staticListIds: ObjectId[];
  emptyState?: React.ReactNode;
}) => {
  const {data: allLists = []} = useTags({}, {refetchOnMount: false});
  const {data: dynamicListIds, isLoading} = useDynamicListMembership(
    {accountId},
    {refetchOnMount: false}
  );

  const listMap = useMemo(
    () => new Map(allLists.map((t) => [t._id.toString(), t])),
    [allLists]
  );

  const resolvedLists = filterNull(
    staticListIds
      .concat(dynamicListIds ?? [])
      .map((id) => listMap.get(id.toString()))
  );

  if (!resolvedLists.length) {
    if (isLoading) {
      return <Skeleton height="20px" width="60px" borderRadius={40} />;
    }
    if (emptyState) {
      return <>{emptyState}</>;
    }
  }

  return (
    <Flex wrap="wrap" gap={2}>
      {resolvedLists.map((list) => (
        <ListPill key={list._id.toString()} list={list} />
      ))}
    </Flex>
  );
};
export const ListsRenderer = React.memo(_ListsRenderer);
